import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  RadioGroup,
  Stack,
  Radio,
} from "@chakra-ui/react";
import useApi from "hooks/useApi";
import { CREATE_USER } from "constants/endpoints";
import { showToast } from 'components/toast/Toast';
import { CenteredSpinner } from "components/loader/Loader";
import { UPDATE_ROLE_API } from "constants/endpoints";

const roleOptions = [
  { id: "1", label: "Individual", value: "owner" },
  { id: "2", label: "Agent", value: "agent_dealer" },
  { id: "3", label: "Builder", value: "builder" },
  { id: "4", label: "Co Living", value: "co_living" },
  { id: "5", label: "Marketing Admin", value: "marketing_admin" },
  { id: "6", label: "Bloging Admin", value: "bloging_admin" },
  { id: "7", label: "Sales Admin", value: "sales_admin" },
];

const genderOptions = [
  {
    id: "1",
    label: 'Male',
    value: 'm'
  },
  {
    id: "2",
    label: 'Female',
    value: 'f'
  },
  {
    id: "3",
    label: 'Other',
    value: 'o'
  }
]

const initialFormState = {
  first_name: '',
  last_name: '',
  email: null,
  phone: '',
  role: 'owner',
  password:'',
  gender: ''
};

const SignupFormModal = ({
  isOpen,
  onClose,
  setOpenLoginForm,
  selectedUser,
  setSelectedUser,
  setUserData
}) => {
  const initialRef = React.useRef(null);
  const finalRef = React.useRef(null);
  const { apiCall, loading } = useApi();
  const [form, setForm] = useState({ ...initialFormState });

  useEffect(() => {
    if (!isOpen) {
      setOpenLoginForm(false);
      setForm(initialFormState);
      setSelectedUser({});
    } 
    if(selectedUser){
      setForm(selectedUser);
    }
  }, [isOpen, setOpenLoginForm]);

  const handleFormSubmit = async () => {
    const isUpdating = form?._id ? true : false;
    const method = isUpdating ? 'patch' : 'post';
  
    try {
      // ✅ Step 1: If updating and the role has changed, update the role first
      if (isUpdating && form.role !== selectedUser?.role) {
        const { data } = await apiCall.patch(UPDATE_ROLE_API, {
          _id: form._id,
          role: form.role,
        });
  
        // ✅ Instantly update the role in the state without refresh
        setUserData(prevUserData =>
          prevUserData.map(item =>
            item._id === form._id ? { ...item, role: form.role } : item
          )
        );
  
        showToast({
          message: 'User role updated successfully',
          success: true,
          customStyle: {
            background: '#52c41a',
            color: '#fff',
            padding: '10px',
          },
        });
  
        onClose();
        setOpenLoginForm(false);
      
        const isOnlyRoleChanged = Object.keys(form).every(key => {
          if (key === 'role') return true; 
          return form[key] === selectedUser[key];
        });
  
        if (isOnlyRoleChanged) {
          return; 
        }
      }
  
      // ✅ Step 2: Create or Update user details
      const user = await apiCall[method](CREATE_USER, {
        ...form,
        role: isUpdating ? undefined : form.role, // Only send role if creating a new user
      });
  
      if (user?.data) {
        setUserData(prevUserData => {
          if (isUpdating) {
            return prevUserData.map(item =>
              item._id === form._id ? { ...item, ...form } : item
            );
          }
          return [user.data.userData, ...prevUserData];
        });
  
        setForm({});
        showToast({
          message: isUpdating ? 'Account updated successfully' : 'Account created successfully',
          success: true,
          customStyle: {
            background: '#52c41a',
            color: '#fff',
            padding: '10px',
          },
        });
  
        onClose();
        setOpenLoginForm(false);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
      showToast({
        message: `Error: ${errorMessage}`,
        success: false,
        customStyle: {
          background: '#F24052',
          color: '#fff',
          padding: '10px',
        },
      });
  
      if (error.response.status !== 409 && error.response.status !== 400) {
        onClose();
        setOpenLoginForm(false);
      }
    }
  };
  
  
  
const handleChange = (e) => {
  const { name, value } = e.target;

  // Trim spaces if the input is for the phone number
  const processedValue = name === 'phone' ? value.trim() : value;

  setForm({ ...form, [name]: processedValue });
};


  const handleRoleChange = (value) => {
    setForm({ ...form, role: value });
  };

  const handleGenderChange = (value) => {
    setForm({ ...form, gender: value });
  };

  const isButtonDisabled = () => {
    return !(form.first_name && form.last_name && form.phone && form.phone.length === 10 && !loading);
  };

  const isPasswordFieldVisible = useMemo(() => {
    return (!form._id && form.role === 'bloging_admin') ||
           (form._id && form.role === 'bloging_admin' && selectedUser?.role !== 'bloging_admin');
  }, [form._id, form.role, selectedUser?.role]);

  return (
    <>
      <Modal
        initialFocusRef={initialRef}
        finalFocusRef={finalRef}
        isOpen={isOpen}
        onClose={onClose}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{!form._id?'Create user account':'Update user account'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={3}>
            <FormControl>
              <FormLabel>Role</FormLabel>
              <RadioGroup value={form.role} onChange={handleRoleChange}>
              <Stack direction="row" flexWrap="wrap" mr={3} width="100%" alignItems="flex-start">
                  {roleOptions.map((option) => (
                    <Radio key={option.id} value={option.value} mb={3}>
                      {option.label}
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>
            <FormControl mt={3} isRequired>
              <FormLabel>First name</FormLabel>
              <Input
                ref={initialRef}
                placeholder="First name"
                value={form.first_name}
                onChange={handleChange}
                name="first_name"
              />
            </FormControl>

            <FormControl mt={3} isRequired>
              <FormLabel>Last name</FormLabel>
              <Input
                placeholder="Last name"
                value={form.last_name}
                onChange={handleChange}
                name="last_name"
              />
            </FormControl>

            <FormControl mt={3}>
              <FormLabel>Email</FormLabel>
              <Input
                placeholder="Email"
                value={form.email}
                onChange={handleChange}
                name="email"
              />
            </FormControl>
            {loading && <CenteredSpinner/>}
            <FormControl mt={3} isRequired>
              <FormLabel>Phone Number</FormLabel>
              <Input
                placeholder="Phone Number"
                value={form.phone}
                onChange={handleChange}
                name="phone"
                maxLength={10}
              />
            </FormControl>
             <FormControl>
              <FormLabel>Gender</FormLabel>
              <RadioGroup value={form.gender} onChange={handleGenderChange}>
              <Stack direction="row" flexWrap="wrap" mr={3} width="100%" alignItems="flex-start">
                  {genderOptions.map((option) => (
                    <Radio key={option.id} value={option.value} mb={3}>
                      {option.label}
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>
            {isPasswordFieldVisible && (
              <FormControl mt={3} isRequired>
                <FormLabel>Password</FormLabel>
                <Input
                  placeholder="Enter new password"
                  value={form.password}
                  onChange={handleChange}
                  name="password"
                />
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              background={"linear-gradient(180deg, #22ACEE 0%, #0177B4 100%)"}
              color="#ffffff"
              mr={3}
              onClick={handleFormSubmit}
              disabled={isButtonDisabled()}
            >
              Save
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SignupFormModal;
